import { useEffect, useState } from "react";
import { LogIn, LogOut, Loader2, RefreshCw, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { fetchVolunteers, fetchContacts } from "@/lib/api";

const TOKEN_KEY = "rob4nh.admin.token";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(() => !!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("volunteers");
  const [loading, setLoading] = useState(false);
  const [volunteers, setVolunteers] = useState([]);
  const [contacts, setContacts] = useState([]);

  const load = async (tok) => {
    setLoading(true);
    try {
      const [v, c] = await Promise.all([fetchVolunteers(tok), fetchContacts(tok)]);
      setVolunteers(Array.isArray(v) ? v : []);
      setContacts(Array.isArray(c) ? c : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Invalid admin token.");
        signOut();
      } else if (status === 503) {
        toast.error("Admin access is not configured on the server.");
      } else {
        toast.error("Failed to load submissions.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed && token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const signIn = async (e) => {
    e.preventDefault();
    if (!token.trim()) return toast.error("Enter your admin token.");
    localStorage.setItem(TOKEN_KEY, token.trim());
    setAuthed(true);
  };

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setVolunteers([]);
    setContacts([]);
  };

  if (!authed) {
    return (
      <main className="section" data-testid="admin-login-page">
        <div className="container-x max-w-md">
          <p className="overline">Admin</p>
          <h1 className="mt-4 font-display text-4xl text-ink">Sign in.</h1>
          <p className="mt-3 text-ink/70">
            Enter the admin token to view volunteer and contact submissions.
          </p>
          <form onSubmit={signIn} className="mt-8 card-flat space-y-4">
            <label className="overline" htmlFor="token">Admin token</label>
            <input
              id="token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="admin-token-input"
              className="w-full rounded-xl border border-rule bg-white/5 px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-forest/40 focus:border-forest"
              autoFocus
            />
            <button type="submit" className="btn-primary w-full justify-center" data-testid="admin-signin-btn">
              <LogIn size={16} /> Sign in
            </button>
          </form>
        </div>
      </main>
    );
  }

  const list = tab === "volunteers" ? volunteers : contacts;

  return (
    <main className="section" data-testid="admin-page">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-rule pb-8">
          <div>
            <p className="overline">Admin</p>
            <h1 className="mt-3 font-display text-4xl text-ink">Submissions</h1>
            <p className="mt-2 text-ink/65 text-sm">
              {volunteers.length} volunteers · {contacts.length} contact messages
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => load(token)}
              className="btn-ghost"
              disabled={loading}
              data-testid="admin-refresh-btn"
            >
              {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
              Refresh
            </button>
            <button onClick={signOut} className="btn-ghost" data-testid="admin-signout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-2 border-b border-rule" data-testid="admin-tabs">
          <button
            onClick={() => setTab("volunteers")}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === "volunteers" ? "border-terracotta text-ink" : "border-transparent text-ink/55 hover:text-ink"
            }`}
            data-testid="admin-tab-volunteers"
          >
            Volunteers ({volunteers.length})
          </button>
          <button
            onClick={() => setTab("contacts")}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === "contacts" ? "border-terracotta text-ink" : "border-transparent text-ink/55 hover:text-ink"
            }`}
            data-testid="admin-tab-contacts"
          >
            Messages ({contacts.length})
          </button>
        </div>

        {loading && list.length === 0 ? (
          <p className="mt-12 text-ink/60">Loading…</p>
        ) : list.length === 0 ? (
          <p className="mt-12 text-ink/60" data-testid="admin-empty">
            No {tab} submissions yet.
          </p>
        ) : tab === "volunteers" ? (
          <ul className="mt-8 space-y-4" data-testid="admin-volunteers-list">
            {volunteers.map((v) => (
              <li key={v.id} className="card-flat" data-testid={`admin-volunteer-${v.id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-2xl text-ink">{v.name}</h3>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-granite">
                      <a href={`mailto:${v.email}`} className="inline-flex items-center gap-1 hover:text-forest">
                        <Mail size={13} /> {v.email}
                      </a>
                      {v.phone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone size={13} /> {v.phone}
                        </span>
                      )}
                      {v.town && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={13} /> {v.town}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-xs font-mono text-granite shrink-0">{fmtDate(v.created_at)}</span>
                </div>
                {Array.isArray(v.interests) && v.interests.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {v.interests.map((i) => (
                      <span key={i} className="px-3 py-1 rounded-xl border border-forest/40 text-forest text-xs">
                        {i}
                      </span>
                    ))}
                  </div>
                )}
                {v.availability && (
                  <p className="mt-4 text-sm text-ink/80">
                    <span className="overline mr-2">Availability</span>
                    {v.availability}
                  </p>
                )}
                {v.message && (
                  <p className="mt-4 text-ink/85 whitespace-pre-wrap border-l-2 border-terracotta pl-4">
                    {v.message}
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <ul className="mt-8 space-y-4" data-testid="admin-contacts-list">
            {contacts.map((c) => (
              <li key={c.id} className="card-flat" data-testid={`admin-contact-${c.id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-2xl text-ink">{c.subject || "(no subject)"}</h3>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-granite">
                      <span>{c.name}</span>
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-forest">
                        <Mail size={13} /> {c.email}
                      </a>
                    </div>
                  </div>
                  <span className="text-xs font-mono text-granite shrink-0">{fmtDate(c.created_at)}</span>
                </div>
                <p className="mt-4 text-ink/85 whitespace-pre-wrap border-l-2 border-terracotta pl-4">
                  {c.message}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
};

export default Admin;
